import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { 
  Sparkles, 
  ArrowRight,
  Lightbulb,
  Heart,
  Globe,
  Leaf,
  Shield,
  Zap
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const values = [
  {
    icon: Lightbulb,
    title: 'AI as Foundation',
    description: 'We don\'t add AI features to CAD. We built CAD on top of AI. This fundamental architectural decision enables capabilities impossible for retrofitted solutions.',
  },
  {
    icon: Heart,
    title: 'Maker-First Design',
    description: 'Every feature is designed by makers, for makers. We understand chamfers, not just edges. We speak G-code, not just code.',
  },
  {
    icon: Shield,
    title: 'Transparent AI',
    description: 'You control the AI, not the other way around. Every suggestion can be explained, every decision can be overridden. No black boxes.',
  },
  {
    icon: Leaf,
    title: 'Sustainable by Design',
    description: 'AI optimizes every print for minimal waste, suggests recyclable materials, and calculates carbon footprint. Making should be responsible.',
  },
]

const milestones = [
  {
    phase: 'Phase 1',
    title: 'Foundation',
    status: 'current',
    items: ['AI-native CAD kernel', 'Core design workflows', 'Print prediction beta', 'Early partnerships'],
  },
  {
    phase: 'Phase 2',
    title: 'Intelligence',
    status: 'upcoming',
    items: ['Text-to-3D generation', 'Scan-to-CAD', 'Real-time collaboration', 'Marketplace launch'],
  },
  {
    phase: 'Phase 3',
    title: 'Scale',
    status: 'future',
    items: ['Fleet management', 'Print-on-demand network', 'Enterprise features', 'Global expansion'],
  },
  {
    phase: 'Phase 4',
    title: 'Ecosystem',
    status: 'future',
    items: ['Advanced AI models', 'Industry verticals', 'Hardware partnerships', 'Developer platform'],
  },
]


export default function AboutPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ai-500/10 border border-ai-500/20 mb-8"
            >
              <Globe className="w-4 h-4 text-ai-400" />
              <span className="text-sm font-medium text-ai-300">Our Mission</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            >
              Reimagining what CAD
              <br />
              <span className="text-gradient">becomes with intelligence</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-surface-300 max-w-2xl leading-relaxed"
            >
              3DMakerly is the world's first AI-native 3D manufacturing platform. 
              Every interaction - from first sketch to final print - is augmented by 
              machine intelligence that understands design intent, predicts outcomes, 
              and continuously learns from the collective experience of millions of prints.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Vision Statement */}
      <section className="py-24 bg-surface-900/30 border-y border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                We don't add AI to CAD.
                <br />
                <span className="text-gradient">We reimagine what CAD becomes when intelligence is foundational.</span>
              </h2>
              <p className="text-surface-300 leading-relaxed mb-6">
                Traditional CAD applications bolt AI features onto legacy architectures. 
                3DMakerly inverts this paradigm: the AI system is the platform, with CAD 
                operations as one of many interfaces to underlying intelligence.
              </p>
              <p className="text-surface-300 leading-relaxed">
                This isn't incremental improvement. It's a fundamental rethinking of 
                how software can understand and augment the creative process of making 
                physical things.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-surface-800 to-surface-900 border border-surface-700 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-ai-500 mb-6">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-2xl font-bold text-white mb-2">AI-Native</p>
                  <p className="text-surface-400">Not AI-Added</p>
                </div>
              </div>
              <div className="absolute -inset-4 bg-gradient-to-br from-primary-500/10 to-ai-500/10 rounded-3xl blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">What we believe</h2>
            <p className="text-surface-400 max-w-2xl mx-auto">
              Our core values guide every decision, from architecture to UX to business model.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-surface-900/50 border border-surface-700/50"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-500/10 mb-6">
                  <value.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-surface-400 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Roadmap */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Building the future</h2>
            <p className="text-surface-400 max-w-2xl mx-auto">
              Our roadmap to transform 3D manufacturing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.phase}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-xl border ${
                  milestone.status === 'current'
                    ? 'bg-gradient-to-b from-primary-900/30 to-surface-900 border-primary-500/50'
                    : 'bg-surface-900/50 border-surface-700/50'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    milestone.status === 'current'
                      ? 'bg-primary-500/20 text-primary-400'
                      : 'bg-surface-700 text-surface-400'
                  }`}>
                    {milestone.phase}
                  </span>
                  {milestone.status === 'current' && (
                    <span className="flex items-center gap-1 text-xs text-success-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-success-400 animate-pulse" />
                      In Progress
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white mb-4">{milestone.title}</h3>
                <ul className="space-y-2">
                  {milestone.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-surface-400">
                      <span className="w-1 h-1 rounded-full bg-surface-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us */}
      <section className="py-24 bg-surface-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Join us in building the future of making
            </h2>
            <p className="text-xl text-surface-300 mb-10 max-w-2xl mx-auto">
              We're assembling a world-class team of engineers, designers, and makers 
              who believe software can fundamentally transform how physical things are created.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button variant="ai" size="lg">
                  <Sparkles className="w-5 h-5" />
                  Try 3DMakerly Free
                </Button>
              </Link>
              <Button variant="secondary" size="lg">
                View Open Roles
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
